import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DIALOG_DIRECTIVES } from '@modules/components/dialog/dialog';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { CheckboxComponent } from '@modules/components/checkbox/checkbox.component';
import { TooltipDirective } from '@modules/components/tooltip/tooltip.directive';
import { ButtonComponent } from '@modules/components/button/button.component';
import { MultiSelectComponent } from '@modules/components/multi-select/multi-select.component';
import { dictionaryToOptions } from '@modules/components/select/select-options';
import { SelectComponent } from '@modules/components/select/select.component';
import { USER } from '@tokens/user.token';
import { RegExpList } from '@utils/reg-exp';
import { NamedId } from '@type/name-id.type';
import { UserPermissionValue, UserSaveRequest } from '@type/user.type';
import { firstValueFrom, map, startWith, switchMap } from 'rxjs';
import { DictionaryService } from 'src/app/core/services/dictionary/dictionary.service';
import { CompaniesService } from '../../services/companies.service';
import { CompanyStateService } from '../../services/company-state.service';
import { UsersService } from '../users-list/services/users/users.service';
import { UserFormService } from './service/user-form.service';
import { FormFieldComponent } from '@modules/components/form-field/form-field.component';

@Component({
  selector: 'app-invite-dialog-user',
  imports: [
    FormFieldComponent,
    ...DIALOG_DIRECTIVES,
    SelectComponent,
    TooltipDirective,
    ReactiveFormsModule,
    CheckboxComponent,
    MultiSelectComponent,
    ButtonComponent,
  ],
  templateUrl: './invite-dialog-user.component.html',
  styleUrls: ['./invite-dialog-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteDialogUserComponent implements OnInit, OnDestroy {
  private usersApiService = inject(UsersService);

  private dialogRef = inject(DialogRef);

  private dictionaryService = inject(DictionaryService);

  private companiesService = inject(CompaniesService);

  company = inject(CompanyStateService).company;

  #currentUser = inject(USER);

  showSqlQueryAccess = computed(() => {
    if (this.company()?.id) return false;
    return this.data.form ?
        this.data.form.sql_query_access !== undefined
      : this.#currentUser().sql_query_access !== undefined;
  });

  protected showSqlWriteAccess = computed(() => {
    if (!this.showSqlQueryAccess()) return false;
    return this.data.form ?
        this.data.form.sql_write_access !== undefined
      : this.#currentUser().sql_write_access !== undefined;
  });

  data = inject(DIALOG_DATA);

  form = inject(UserFormService).form;

  protected roleOptions = computed(() => this.usersRole().map(({ name }) => ({ value: name, label: name })));

  usersRole = toSignal(
    this.dictionaryService
      .getDictionary(
        this.dictionaryService.createRequest({
          q:
            this.company()?.id ?
              { role_type_eq: !this.data.form ? 'admin' : '' }
            : ({ role_type_in: ['super_admin', 'super_user'] } as any),
          model_name: 'role',
          company: this.company()?.id ? String(this.company()?.id) : '',
        }),
      )
      .pipe(map((res) => res['data'])),
    {
      initialValue: [],
    },
  );

  private companiesList = toSignal(this.companiesService.getCompaniesList().pipe(map(({ companies }) => companies)), {
    initialValue: [],
  });

  protected companyOptions = computed(() => dictionaryToOptions(this.companiesList()));

  private email = toSignal(this.form.controls.email.valueChanges, { initialValue: this.form.controls.email.value });

  protected emailError = computed(() =>
    RegExpList.nonLatin.test(this.email()) ? 'Email contains non-Latin characters' : 'Enter a valid email',
  );

  protected isInvalid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((status) => status !== 'VALID'),
    ),
    { initialValue: true },
  );

  private role = toSignal(this.form.controls.role.valueChanges.pipe(startWith(this.form.controls.role.value)));

  private sqlQueryAccess = toSignal(
    this.form.controls.sqlQueryAccess.valueChanges.pipe(startWith(this.form.controls.sqlQueryAccess.value)),
  );

  constructor() {
    effect(() => this.syncCompaniesWithRole());
    effect(() => this.syncSqlWriteWithQueryAccess());
  }

  ngOnInit() {
    this.patchFormValue();
  }

  ngOnDestroy(): void {
    this.form.controls.companyIdList.enable({ emitEvent: false });
    this.form.controls.sqlWriteAccess.enable({ emitEvent: false });
    this.form.reset();
  }

  private patchFormValue() {
    if (this.data.form) {
      this.form.patchValue({
        first_name: this.data.form.first_name,
        last_name: this.data.form.last_name,
        email: this.data.form.email,
        role: this.data.form.role.name,
        companyIdList: this.data.form.available_company_ids?.map((c: NamedId) => c.id),
        sqlQueryAccess: this.data.form.sql_query_access ?? false,
        sqlWriteAccess: this.data.form.sql_write_access ?? false,
      });
    }
  }

  private syncSqlWriteWithQueryAccess() {
    const control = this.form.controls.sqlWriteAccess;

    if (this.sqlQueryAccess()) {
      control.enable({ emitEvent: false });
      return;
    }

    control.setValue(false, { emitEvent: false });
    control.disable({ emitEvent: false });
  }

  private sqlAccessPayload() {
    if (!this.showSqlQueryAccess()) return {};
    return {
      sql_query_access: this.form.controls.sqlQueryAccess.value,
      ...(this.showSqlWriteAccess() && { sql_write_access: this.form.controls.sqlWriteAccess.value }),
    };
  }

  private syncCompaniesWithRole() {
    const control = this.form.controls.companyIdList;

    if (this.company()?.id) return;

    if (this.role() !== UserPermissionValue.SuperAdmin) {
      control.enable({ emitEvent: false });
      return;
    }

    control.setValue(
      this.companiesList().map(({ id }) => id),
      { emitEvent: false },
    );
    control.disable({ emitEvent: false });
  }

  save() {
    if (this.form.invalid) return;

    const companyId = this.company()?.id;

    const buildPayload = (value: Partial<UserSaveRequest>) => {
      //TODO: think about how to use value as an object
      const role = this.usersRole().find((r) => r.name === value.role);
      return {
        id: value?.id,
        first_name: value.first_name,
        last_name: value.last_name,
        email: value.email,
        role_id: role!.id,
        available_company_ids: value.companyIdList,
        // Include SQL access flags directly only in admin context.
        // In company context it goes via a separate admin-endpoint call below,
        // because the proxy forwards to the company backend which ignores the field.
        ...(!companyId && this.sqlAccessPayload()),
      };
    };

    if (this.data.form) {
      let obs = this.usersApiService.updateUser(
        buildPayload(Object.assign(this.data.form, this.form.getRawValue())),
        companyId,
      );

      if (companyId && this.showSqlQueryAccess()) {
        obs = obs.pipe(
          switchMap(() =>
            this.usersApiService.updateUser(
              { id: this.data.form.id, sql_query_access: this.form.controls.sqlQueryAccess.value },
              undefined,
            ),
          ),
        );
      }

      firstValueFrom(obs);
    } else {
      firstValueFrom(this.usersApiService.createUser(buildPayload(this.form.getRawValue()), companyId));
    }

    this.dialogRef.close(true);
  }
}
